import React from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowLeft, FileText } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const TermsPage = () => {
  const sections = [
    {
      title: '1. Acceptance of Terms',
      items: [
        'By accessing or using O4S.ai, you agree to be bound by these Terms and Conditions.',
        'If you do not agree with any part of these terms, please do not use our platform.',
        'We reserve the right to update these terms at any time. Continued use of the platform after changes constitutes acceptance of the updated terms.',
      ],
    },
    {
      title: '2. Use of the Platform',
      items: [
        'O4S.ai is intended for educational and informational purposes only.',
        'You must provide accurate information when creating an account or using our assessment tools.',
        'You agree not to misuse the platform, including attempting to access data belonging to other users, reverse-engineering our systems, or using the platform for any unlawful purpose.',
        'You are responsible for maintaining the confidentiality of your account credentials.',
      ],
    },
    {
      title: '3. No Guarantee of Outcomes',
      items: [
        'O4S.ai provides career guidance and recommendations based on AI analysis and curated data. These recommendations are suggestions only.',
        'We do not guarantee any specific career outcomes, job placements, salary levels, or income results.',
        'Career success depends on many factors including individual effort, market conditions, and personal circumstances that are beyond our control.',
        'The income projections in our Skill2Income feature are estimates based on general market data and should not be treated as guaranteed earnings.',
      ],
    },
    {
      title: '4. User Responsibility',
      items: [
        'You are solely responsible for any career decisions you make based on our recommendations.',
        'We strongly recommend consulting with qualified career counsellors, educators, or professionals before making significant career or educational decisions.',
        'You are responsible for verifying the accuracy and relevance of any information, course recommendations, or salary data provided by our platform.',
      ],
    },
    {
      title: '5. Intellectual Property',
      items: [
        'All content on O4S.ai, including text, graphics, logos, designs, FAQs, career roadmaps, and AI-generated reports, is the intellectual property of O4S.ai or its content creators.',
        'You may share your personal assessment results and Skill2Income cards for personal use, but you may not reproduce, distribute, or commercially exploit any content from this platform without written permission.',
        'The O4S.ai name, logo, and branding are trademarks of O4S.ai.',
      ],
    },
    {
      title: '6. Account Termination',
      items: [
        'We reserve the right to suspend or terminate your account if you violate these terms or engage in behaviour that is harmful to other users or the platform.',
        'You may delete your account at any time by contacting us.',
      ],
    },
    {
      title: '7. Limitation of Liability',
      items: [
        'O4S.ai is provided "as is" without warranties of any kind, either express or implied.',
        'We are not liable for any direct, indirect, incidental, or consequential damages arising from your use of the platform.',
        'We are not responsible for the accuracy of third-party content, course information, or external links referenced in our recommendations.',
      ],
    },
    {
      title: '8. Governing Law',
      items: [
        'These Terms and Conditions are governed by the laws of India.',
        'Any disputes arising from the use of this platform shall be subject to the jurisdiction of courts in Hyderabad, Telangana, India.',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="Terms & Conditions — O4S.ai" path="/terms" />

      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 py-3 border-b border-earth-200">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-earth-500 hover:text-earth-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-earth-700">O4S.ai</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-7 h-7 text-primary" />
          <h1 className="text-3xl sm:text-4xl font-bold text-earth-700">Terms & Conditions</h1>
        </div>
        <p className="text-earth-500 text-sm mb-10">Last updated: March 2026</p>

        <p className="text-earth-600 leading-relaxed mb-8">Please read these Terms and Conditions carefully before using O4S.ai. By using our platform, you acknowledge that you have read, understood, and agree to these terms.</p>

        {sections.map((section, i) => (
          <section key={i} className="mb-8">
            <h2 className="text-lg font-bold text-earth-700 mb-3">{section.title}</h2>
            <ul className="space-y-2">
              {section.items.map((item, j) => (
                <li key={j} className="text-earth-600 text-sm leading-relaxed flex gap-2">
                  <span className="text-primary mt-1 flex-shrink-0">-</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <p className="text-earth-500 text-sm border-t border-earth-200 pt-6">For questions about these Terms, please visit our <Link to="/contact" className="text-primary underline">Contact page</Link>.</p>
      </main>

      <footer className="px-4 md:px-8 py-8 bg-earth-700 text-white mt-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm mb-4">
            <Link to="/about" className="text-earth-400 hover:text-white transition-colors">About</Link>
            <Link to="/founder" className="text-earth-400 hover:text-white transition-colors">Founder</Link>
            <Link to="/contact" className="text-earth-400 hover:text-white transition-colors">Contact</Link>
            <Link to="/privacy" className="text-earth-400 hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-earth-400 hover:text-white transition-colors">Terms</Link>
            <Link to="/legal" className="text-earth-400 hover:text-white transition-colors">Legal</Link>
          </div>
          <p className="text-earth-500 text-xs text-center">&copy; 2026 O4S.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
